import { useRef, useState } from 'react';
import {
  Alert, Box, Dialog, DialogActions, DialogContent, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField, Tooltip, Button,
} from '@mui/material';
import React from 'react';
import { AccountTree, CellTower, Explore, Flag, MyLocation, Shield, BadgeOutlined } from '@mui/icons-material';

import { useAuthContext } from '../../context/auth';
import { useUnitContext } from '../../context/unit';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDialogStyles from '../../hooks/useDialogStyles';
import { AddButton, DialogHeader, FormSection, SubmitButton } from '../dialogKit';
import PositionPicker from '../map/PositionPicker';

// Unit types: 0 Command, 1 Regiment, 2 Company, 3 Outpost
export const UNIT_TYPES = [
  { label: 'Command', Icon: Shield, subtitle: 'Top level of the unit tree' },
  { label: 'Regiment', Icon: AccountTree, subtitle: 'Belongs to a command' },
  { label: 'Company', Icon: Flag, subtitle: 'Belongs to a regiment' },
  { label: 'Outpost', Icon: CellTower, subtitle: 'Radar station: position and calibration' },
];

const Add = ({ type, units }) => {
  const axiosPrivate = useAxiosPrivate();
  const { dispatch } = useUnitContext();
  const { auth } = useAuthContext();
  const ds = useDialogStyles();

  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const nameRef = useRef('');
  const numberRef = useRef('');
  // Position is shared by the two fields and the map picker
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const accuracyRef = useRef('0.8');
  const trueNorthRef = useRef('0');
  const distanceRateRef = useRef('1');

  const unitType = UNIT_TYPES[type] || { label: 'Unit', Icon: Shield };
  const label = unitType.label;
  const parentLabel = type > 0 ? UNIT_TYPES[type - 1].label : null;

  const [selectedParentUnit, setSelectedParentUnit] = useState(null);

  const parents = (units || [])
    .filter(unit => unit.type === type - 1)
    .sort((a, b) => a.name.localeCompare(b.name));
  const noParents = type > 0 && parents.length === 0;

  const closeDialog = () => {
    if (saving) return;
    setShow(false);
    setError(null);
  };

  const handleAdd = async () => {
    if (!auth) {
      setError('You must be logged in');
      return;
    }

    const newUnit = {
      type: type,
      name: nameRef.current.value,
      parentId: selectedParentUnit || null, // Use selected parent unit or null if none selected
      number: numberRef.current.value,
      latitude,
      longitude,
      accuracy: accuracyRef.current.value,
      trueNorth: trueNorthRef.current.value,
      distanceRate: distanceRateRef.current.value,
    };

    setSaving(true);
    try {
      const response = await axiosPrivate.post('/api/units', newUnit);
      dispatch({ type: 'CREATE_UNIT', payload: response.data });
      setError(null);
      setShow(false);
    } catch (error) {
      setError(error.response?.data.error || `Unable to create the ${label.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const numberField = (fieldLabel, ref, { unit, defaultValue, helper } = {}) => (
    <TextField
      size="small"
      label={fieldLabel}
      inputRef={ref}
      type="number"
      defaultValue={defaultValue}
      helperText={helper}
      InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
      sx={ds.fieldSx}
    />
  );

  return (
    <>
      {/* span: a disabled button gets no mouse events, so the tooltip needs a wrapper */}
      <Tooltip title={noParents ? `Add a ${parentLabel.toLowerCase()} first` : ''} arrow>
        <span>
          <AddButton
            label="Add"
            onClick={() => {
              setShow(true);
              setError(null);
              setLatitude('');
              setLongitude('');
            }}
            disabled={noParents}
          />
        </span>
      </Tooltip>

      <Dialog
        open={show}
        onClose={closeDialog}
        fullWidth
        maxWidth={type === 3 ? 'sm' : 'xs'}
        PaperProps={{ sx: ds.paperSx }}
      >
        <DialogHeader icon={unitType.Icon} title={`New ${label.toLowerCase()}`} subtitle={unitType.subtitle} onClose={closeDialog} disabled={saving} />

        <DialogContent sx={ds.contentSx}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: type === 3 ? '1fr 1fr' : '1fr' }, gap: 2 }}>
            {type === 3 && <FormSection icon={BadgeOutlined} title="Identity" />}

            <TextField
              fullWidth
              size="small"
              label="Name"
              inputRef={nameRef}
              autoComplete="off"
              autoFocus
              sx={ds.fieldSx}
            />

            {type > 0 && (
              <FormControl fullWidth size="small" sx={ds.fieldSx}>
                <InputLabel>{`Parent ${parentLabel.toLowerCase()}`}</InputLabel>
                <Select
                  label={`Parent ${parentLabel.toLowerCase()}`}
                  value={selectedParentUnit || ''}
                  onChange={(e) => setSelectedParentUnit(e.target.value)}
                  MenuProps={ds.menuProps}
                >
                  {parents.map(unit => (
                    <MenuItem key={unit._id} value={unit._id}>
                      {unit.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {type === 3 && (
              <>
                {numberField('Outpost number', numberRef, { helper: 'Number sent by the radar' })}
                <Box />

                <FormSection icon={MyLocation} title="Position" hint="type it or pick it on the map" />
                <TextField
                  size="small"
                  label="Latitude"
                  type="number"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end">°</InputAdornment> }}
                  sx={ds.fieldSx}
                />
                <TextField
                  size="small"
                  label="Longitude"
                  type="number"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end">°</InputAdornment> }}
                  sx={ds.fieldSx}
                />
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <PositionPicker
                    lat={latitude}
                    lng={longitude}
                    onChange={(la, ln) => { setLatitude(la); setLongitude(ln); }}
                    others={(units || [])
                      .filter((u) => u.type === 3)
                      .map((u) => ({ id: u._id, name: u.name, latitude: u.latitude, longitude: u.longitude }))}
                  />
                </Box>

                <FormSection icon={Explore} title="Radar calibration" />
                {numberField('True north', trueNorthRef, { unit: '°', defaultValue: 0 })}
                {numberField('Accuracy', accuracyRef, { defaultValue: 0.8 })}
                {numberField('Distance rate', distanceRateRef, { defaultValue: 1, helper: 'Multiplier for radar distances' })}
              </>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={ds.actionsSx}>
          <Button onClick={closeDialog} disabled={saving} sx={ds.cancelButtonSx}>
            Cancel
          </Button>
          <SubmitButton label={`Add ${label}`} busyLabel="Creating…" busy={saving} onClick={handleAdd} icon={<unitType.Icon />} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Add;
